/**
 * Addressbook Domain Constants
 * All field names, translation keys, and API fields in one place
 */

// ============================================================================
// Pagination
// ============================================================================

export const DEFAULT_PAGE_SIZE = 10
export const MAX_PAGE_SIZE = 1000

// ============================================================================
// Address Types
// ============================================================================

export const ADDRESS_TYPES = ['site', 'office', 'warehouse', 'home', 'plant', 'customer'] as const
export type AddressTypeValue = typeof ADDRESS_TYPES[number]

// ============================================================================
// Form Field Names
// ============================================================================

export const ADDRESS_FIELDS = {
  NAME: 'name',
  TYPE: 'type',
  LOCATION_TYPE_ID: 'locationTypeId',
  ADDRESS_LINE_1: 'addressLine1',
  ADDRESS_LINE_2: 'addressLine2',
  CITY_ID: 'cityId',
  COUNTRY_ID: 'countryId',
  POSTAL_CODE: 'postalCode',
  TIMEZONE: 'timezone',
  CONTACT_NAME: 'contactName',
  CONTACT_PHONE: 'contactPhone',
  LATITUDE: 'latitude',
  LONGITUDE: 'longitude',
  IS_DEFAULT: 'isDefault',
} as const

export type AddressFieldName = typeof ADDRESS_FIELDS[keyof typeof ADDRESS_FIELDS]

// ============================================================================
// Translation Keys
// ============================================================================

export const T = {
  // Page titles
  TITLE: 'addressbook.title',
  DESCRIPTION: 'addressbook.description',

  // Actions
  ADD_ADDRESS: 'addressbook.addAddress',
  EDIT_ADDRESS: 'addressbook.editAddress',
  ADD_ADDRESS_DESCRIPTION: 'addressbook.addAddressDescription',
  EDIT_ADDRESS_DESCRIPTION: 'addressbook.editAddressDescription',

  // Sections
  BASIC_INFO: 'addressbook.basicInfo',
  ADDRESS_DETAILS: 'addressbook.addressDetails',
  CONTACT_INFO: 'addressbook.contactInfo',
  COORDINATES: 'addressbook.coordinates',

  // Field labels
  LOCATION_NAME: 'addressbook.locationName',
  LOCATION_NAME_PLACEHOLDER: 'addressbook.locationNamePlaceholder',
  LOCATION_TYPE: 'addressbook.locationType',
  SELECT_TYPE: 'addressbook.selectType',
  STREET_ADDRESS: 'addressbook.streetAddress',
  STREET_ADDRESS_PLACEHOLDER: 'addressbook.streetAddressPlaceholder',
  ADDRESS_LINE_2: 'addressbook.form.addressLine2',
  ADDRESS_LINE_2_PLACEHOLDER: 'addressbook.form.addressLine2.placeholder',
  COUNTRY: 'addressbook.country',
  SELECT_COUNTRY: 'addressbook.selectCountry',
  CITY: 'addressbook.city',
  SELECT_CITY: 'addressbook.selectCity',
  SELECT_COUNTRY_FIRST: 'addressbook.selectCountryFirst',
  NO_CITIES_AVAILABLE: 'addressbook.noCitiesAvailable',
  POSTAL_CODE: 'addressbook.postalCode',
  POSTAL_CODE_PLACEHOLDER: 'addressbook.postalCodePlaceholder',
  TIMEZONE: 'addressbook.timezone',
  CONTACT_NAME: 'addressbook.contactName',
  CONTACT_NAME_PLACEHOLDER: 'addressbook.contactNamePlaceholder',
  CONTACT_PHONE: 'addressbook.contactPhone',
  CONTACT_PHONE_PLACEHOLDER: 'addressbook.contactPhonePlaceholder',
  LATITUDE: 'addressbook.latitude',
  LONGITUDE: 'addressbook.longitude',

  // Address types
  SITE: 'addressbook.site',
  OFFICE: 'addressbook.office',
  WAREHOUSE: 'addressbook.warehouse',
  HOME: 'addressbook.home',
  PLANT: 'addressbook.plant',
  CUSTOMER: 'addressbook.customer',

  // Status
  ACTIVE: 'addressbook.active',
  INACTIVE: 'addressbook.inactive',
  ALL: 'addressbook.all',

  // Validation
  VALIDATION: {
    NAME_REQUIRED: 'addressbook.validation.nameRequired',
    ADDRESS_REQUIRED: 'addressbook.validation.addressRequired',
    COUNTRY_REQUIRED: 'addressbook.validation.countryRequired',
    CITY_REQUIRED: 'addressbook.validation.cityRequired',
    TYPE_REQUIRED: 'addressbook.validation.typeRequired',
  },

  // Stats
  STATS: {
    TOTAL_ADDRESSES: 'addressbook.stats.totalAddresses',
    ACTIVE_LOCATIONS: 'addressbook.stats.activeLocations',
    INACTIVE_LOCATIONS: 'addressbook.stats.inactiveLocations',
    BY_TYPE: 'addressbook.stats.byType',
  },

  // Empty state
  EMPTY: {
    TITLE: 'addressbook.empty.title',
    DESCRIPTION: 'addressbook.empty.description',
  },
} as const

// ============================================================================
// API Field Names (match Strapi schema)
// ============================================================================

export const API_FIELDS = {
  // Location fields
  ID: 'id',
  DOCUMENT_ID: 'documentId',
  CODE: 'code',
  NAME: 'name',
  ADDRESS_LINE_1: 'addressLine1',
  ADDRESS_LINE_2: 'addressLine2',
  POSTAL_CODE: 'postalCode',
  TIMEZONE: 'timezone',
  LATITUDE: 'latitude',
  LONGITUDE: 'longitude',
  STATUS: 'status',
  METADATA: 'metadata',

  // Relations
  CITY: 'city',
  COUNTRY: 'country',
  LOCATION_TYPE: 'locationType',
  TENANT: 'tenant',
  TAGS: 'tags',

  // Timestamps
  CREATED_AT: 'createdAt',
  UPDATED_AT: 'updatedAt',
} as const

// ============================================================================
// API Populate Fields
// ============================================================================

export const POPULATE = {
  LOCATION_FULL: ['city', 'country', 'locationType', 'tags', 'tenant'],
  LOCATION_BASIC: ['city', 'country', 'locationType'],
  CITY: ['country'],
} as const

// ============================================================================
// Status Values
// ============================================================================

export const STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  PENDING: 'pending',
} as const

export type StatusValue = typeof STATUS[keyof typeof STATUS]

// ============================================================================
// Filter Values
// ============================================================================

export const FILTER = {
  ALL: 'all',
  ACTIVE: 'active',
  INACTIVE: 'inactive',
} as const
